import React, { useEffect, useState } from 'react';
import '../../../styles/common/PopupConfirmation.css';

interface PopupConfirmationProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isOpen: boolean;
  onClose: () => void;
  autoCloseTime?: number; // Time in milliseconds
  onConfirm?: () => void; // Added for delete confirmation
  isConfirmation?: boolean; // To determine if it's a confirmation popup
}

const PopupConfirmation: React.FC<PopupConfirmationProps> = ({
  message,
  type,
  isOpen,
  onClose,
  autoCloseTime = 3000, // Default 3 seconds
  onConfirm,
  isConfirmation = false
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      
      // Auto close after specified time (only if not a confirmation popup)
      if (!isConfirmation) {
        const timer = setTimeout(() => {
          handleClose();
        }, autoCloseTime);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, autoCloseTime, isConfirmation]);

  const handleClose = () => {
    setVisible(false);
    // Small delay to allow animation to complete
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    handleClose();
  };

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        );
      case 'error':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        );
      case 'warning':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        );
      case 'info':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`popup-overlayforgrn ${visible ? 'visibleforgrn' : ''}`}>
      <div className={`popup-containerforgrn ${type} ${visible ? 'visibleforgrn' : ''}`}>
        <div className="popup-iconforgrn">{getIcon()}</div>
        <div className="popup-messageforgrn">{message}</div>
        {isConfirmation ? (
          <div className="popup-buttonsforgrn">
            <button className="popup-buttonforgrn confirm" onClick={handleConfirm}>OK</button>
            <button className="popup-buttonforgrn cancel" onClick={handleClose}>Cancel</button>
          </div>
        ) : (
          <button className="popup-buttonforgrn" onClick={handleClose}>OK</button>
        )}
      </div>
    </div>
  );
};

export default PopupConfirmation;